import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { savedPromos, type SavedPromo } from '@/mocks/profile';

const calcDaysLeft = (endsAt: string) => {
  const diff = new Date(endsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

const pad = (n: number) => String(n).padStart(2, '0');

const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const MiniCountdown = ({ endsAt, color }: { endsAt: string; color: string }) => {
  const [time, setTime] = useState(() => calcTime(endsAt));
  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return (
    <span className="font-mono text-[10px]" style={{ color }}>
      {pad(time.h)}:{pad(time.m)}:{pad(time.s)}
    </span>
  );
};

const SavedPromoCard = ({
  promo,
  onRemove,
}: {
  promo: SavedPromo;
  onRemove: (id: number) => void;
}) => {
  const [copied, setCopied] = useState(false);
  const days = calcDaysLeft(promo.endsAt);
  const isUrgent = days <= 3;

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (promo.code) {
      navigator.clipboard.writeText(promo.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="group rounded-2xl overflow-hidden transition-all duration-300 flex flex-col"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${promo.color}20`,
      }}
    >
      {/* Top accent */}
      <div
        className="h-0.5"
        style={{ background: `linear-gradient(90deg, ${promo.color}, ${promo.colorSecondary}, transparent)` }}
      />

      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: 140 }}>
        <img
          src={promo.image}
          alt={promo.title}
          className="w-full h-full object-cover object-top opacity-50 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />

        {/* Badge */}
        <div
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full font-mono text-[9px] font-bold"
          style={{ background: promo.badgeColor, color: '#050508' }}
        >
          {promo.badge}
        </div>

        {/* Discount */}
        <div
          className="absolute top-3 right-10 w-11 h-11 flex items-center justify-center rounded-xl font-orbitron font-black text-sm"
          style={{ background: `${promo.color}20`, border: `2px solid ${promo.color}`, color: promo.color }}
        >
          {promo.discount}
        </div>

        {/* Remove button */}
        <button
          onClick={() => onRemove(promo.id)}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all opacity-0 group-hover:opacity-100"
          style={{ background: 'rgba(255,45,120,0.2)', border: '1px solid rgba(255,45,120,0.4)' }}
          title="Удалить из сохранённых"
        >
          <i className="ri-close-line text-xs" style={{ color: '#ff2d78' }} />
        </button>

        {/* Urgent badge */}
        {isUrgent && (
          <div
            className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[9px]"
            style={{ background: 'rgba(255,45,120,0.2)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }}
          >
            <i className="ri-alarm-line" />
            Осталось {days === 0 ? 'менее дня' : `${days} дн.`}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-1 group-hover:opacity-75 transition-opacity">
          {promo.title}
        </h3>
        <p className="font-mono text-xs mb-3" style={{ color: promo.color }}>{promo.subtitle}</p>

        {/* Timer row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-white/30">
            <i className="ri-time-line" style={{ color: promo.color }} />
            До конца:
            <MiniCountdown endsAt={promo.endsAt} color={promo.color} />
          </div>
          <span className="font-mono text-[10px] text-white/25">
            Сохранено {promo.savedAt}
          </span>
        </div>

        {/* Actions */}
        <div className="mt-auto flex flex-col gap-2">
          <Link
            to={`/promos/${promo.promoId}`}
            className="whitespace-nowrap flex items-center justify-center gap-2 py-2.5 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300"
            style={{ background: promo.color, color: '#050508', boxShadow: `0 0 14px ${promo.color}40` }}
          >
            <i className="ri-arrow-right-line" />
            Перейти к акции
          </Link>
          {promo.code && (
            <button
              onClick={handleCopy}
              className="whitespace-nowrap flex items-center justify-center gap-2 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
              style={
                copied
                  ? { background: '#39ff14', color: '#050508' }
                  : { border: `1px dashed ${promo.color}40`, color: promo.color, background: `${promo.color}06` }
              }
            >
              <i className={copied ? 'ri-check-line' : 'ri-coupon-line'} />
              {copied ? 'Скопировано!' : `Промокод: ${promo.code}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProfileSavedPromos = () => {
  const [items, setItems] = useState<SavedPromo[]>(savedPromos);
  const [removed, setRemoved] = useState<number | null>(null);

  const handleRemove = (id: number) => {
    setRemoved(id);
    setTimeout(() => {
      setItems((prev) => prev.filter((p) => p.id !== id));
      setRemoved(null);
    }, 350);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-orbitron font-bold text-xl text-white mb-1">Сохранённые акции</h2>
          <p className="font-mono text-xs text-white/35">
            {items.length} {items.length === 1 ? 'акция' : items.length < 5 ? 'акции' : 'акций'} в закладках
          </p>
        </div>
        <Link
          to="/promos"
          className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all self-start sm:self-auto"
          style={{ border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78', background: 'rgba(255,45,120,0.06)' }}
        >
          <i className="ri-price-tag-3-line" />
          Все акции
        </Link>
      </div>

      {/* Urgency alert */}
      {items.some((p) => calcDaysLeft(p.endsAt) <= 3) && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.25)' }}
        >
          <i className="ri-alarm-warning-line text-base" style={{ color: '#ff2d78' }} />
          <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <span style={{ color: '#ff2d78' }}>Внимание!</span> Некоторые сохранённые акции скоро заканчиваются
          </p>
        </div>
      )}

      {/* Grid */}
      {items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((promo) => (
            <div
              key={promo.id}
              style={{
                opacity: removed === promo.id ? 0 : 1,
                transform: removed === promo.id ? 'scale(0.95)' : 'scale(1)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
              }}
            >
              <SavedPromoCard promo={promo} onRemove={handleRemove} />
            </div>
          ))}
        </div>
      ) : (
        <div
          className="flex flex-col items-center justify-center py-16 rounded-2xl text-center"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}
        >
          <div
            className="w-16 h-16 flex items-center justify-center rounded-2xl mb-4"
            style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)' }}
          >
            <i className="ri-bookmark-line text-2xl" style={{ color: 'rgba(255,45,120,0.5)' }} />
          </div>
          <h3 className="font-orbitron font-bold text-base text-white mb-2">Нет сохранённых акций</h3>
          <p className="font-mono text-xs text-white/35 mb-5 max-w-xs">
            Сохраняйте интересные акции, чтобы не пропустить выгодные предложения
          </p>
          <Link
            to="/promos"
            className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer"
            style={{ background: '#ff2d78', color: '#050508', boxShadow: '0 0 16px rgba(255,45,120,0.4)' }}
          >
            <i className="ri-price-tag-3-line" />
            Смотреть акции
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProfileSavedPromos;
